import React from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import QuickCategories from '../components/QuickCategories';
import FeaturedContractors from '../components/FeaturedContractors';
import HowItWorksSection from '../components/HowItWorksSection';
import CallToActionSection from '../components/CallToActionSection';
import LatestJobsSection from '../components/LatestJobsSection';
import { fetchCategories } from '../api/contractorProfile';
import { fetchProvinces } from '../api/locations';
import { getJobs, getMyJobs } from '../api/job';
import { Category } from '../types/contractor';
import { Province } from '../api/locations';
import { Job } from '../types/job';
import { useAuth } from '../context/AuthContext';

export async function homePageLoader() {
  const baseLoads: Promise<any>[] = [
    fetchCategories(),
    fetchProvinces(),
    getJobs().catch(() => [] as Job[]),
  ];

  // Fetch the client's job count to power the onboarding banner
  const myJobsPromise = getMyJobs().catch(() => [] as Job[]);

  const [categories, provinces, allJobs, myJobs] = await Promise.all([
    ...baseLoads,
    myJobsPromise,
  ]);

  // Latest 6 open jobs
  const latestJobs = (allJobs as Job[])
    .filter(j => j.status === 'Open')
    .slice(0, 6);

  return { categories, provinces, latestJobs, myJobCount: (myJobs as Job[]).length };
}

const ClientOnboardingBanner: React.FC<{ onDismiss?: () => void }> = ({ onDismiss }) => {
  const navigate = useNavigate();
  return (
    <div className="mx-container-margin mb-6 bg-secondary-container rounded-xl p-4 flex items-start gap-3 max-w-7xl md:mx-auto">
      <span className="material-symbols-outlined text-secondary flex-shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>
        tips_and_updates
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-label-bold text-on-secondary-container">Post your first job</p>
        <p className="text-sm text-on-secondary-container/80 mt-0.5">
          Describe what you need and contractors will reach out directly.
        </p>
      </div>
      <button
        onClick={() => navigate('/jobs/new')}
        className="flex-shrink-0 bg-secondary text-on-secondary px-4 py-2 rounded-lg text-sm font-label-bold hover:bg-secondary/90 transition-colors"
      >
        Post a Job
      </button>
    </div>
  );
};

const HomePage: React.FC = () => {
  const { categories, provinces, latestJobs, myJobCount } = useLoaderData() as {
    categories: Category[];
    provinces: Province[];
    latestJobs: Job[];
    myJobCount: number;
  };
  const { currentUser } = useAuth();

  const isContractor = currentUser?.isContractor ?? false;
  const isClient = currentUser && !isContractor && !currentUser.isAdmin;
  const showFab = !currentUser || isClient;
  const showOnboardingBanner = isClient && myJobCount === 0;

  return (
    <div className="bg-background text-on-background min-h-screen pb-24 md:pb-0">
      <TopAppBar />
      <main>
        <HeroSection categories={categories} provinces={provinces} />
        {showOnboardingBanner && <ClientOnboardingBanner />}
        <QuickCategories categories={categories} />
        <FeaturedContractors />
        <HowItWorksSection />
        <LatestJobsSection jobs={latestJobs} />
        <CallToActionSection />
      </main>
      <Footer />
      <BottomNavBar />
      {showFab && (
        <Link
          to="/jobs/new"
          className="fixed right-6 bottom-24 w-14 h-14 bg-secondary text-on-secondary rounded-full shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform z-40 md:hidden"
        >
          <span className="material-symbols-outlined text-[28px]" data-icon="add">add</span>
        </Link>
      )}
    </div>
  );
};

export default HomePage;
